import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { IChat, ChatterType } from 'src/app/interface/chat';
import { Subscription } from 'rxjs';
import { IMessage } from 'src/app/interface/message';
import { ChatService } from 'src/app/service/chat/chat.service';
import { AlertService } from 'src/app/service/alert/alert.service';
import { AuthService } from 'src/app/service/auth/auth.service';
import { ScrollPaginationService } from 'src/app/service/scroll-pagination/scroll-pagination.service';
import { MessageSentService } from 'src/app/event/message-sent/message-sent.service';
import { MessageReadService } from 'src/app/event/message-read/message-read.service';
import { IUser } from 'src/app/interface/user';
import { StringHelper } from 'src/app/helper/string/string-helper';
import { ShiftService } from 'src/app/service/shift/shift.service';
import { Keyboard } from '@capacitor/keyboard';

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagesComponent implements OnInit, OnDestroy {

  private messageSentSubscription: Subscription;
  private messageReadSubscription: Subscription;
  private scrollSubscription: Subscription;

  private page: number;
  public requesting: boolean;
  public messages: Array<IMessage>;
  public error: string;
  public message: string;
  public autoScrollLocked: boolean;
  public messagesReadPromise: any;
  public blockInputFocus: boolean;

  @Output()
  public dismiss = new EventEmitter();

  @Input()
  public chat: IChat;

  @ViewChild(ElementRef)
  public ionRefresher: ElementRef;

  constructor(
    private chatService: ChatService,
    private alertService: AlertService,
    private scrollPaginationService: ScrollPaginationService,
    private authService: AuthService,
    private messageSentService: MessageSentService,
    private messageReadService: MessageReadService,
    private shiftService: ShiftService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  public get isGroup(): boolean {
    return this.chat?.chatter_type === ChatterType.Group;
  }

  public get user(): IUser {
    return this.authService.user;
  }

  public ngOnInit() {
    this.index(true);
    this.scrollListener();
    this.messageSentListener();
    this.messageReadListeners();

  }

  private scrollListener() {
    this.scrollSubscription = this.scrollPaginationService
      .listener(document.getElementById('messagesContainer'), true)
      .subscribe(() => this.index());
  }

  public ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
    if (this.messageSentSubscription) {
      this.messageSentSubscription.unsubscribe();
    }
    if (this.messageReadSubscription) {
      this.messageReadSubscription.unsubscribe();
    }
  }

  public index(reset?: boolean) {
    if (reset) {
      this.page = 1;
    }
    if (this.page <= 0 || this.requesting) {
      return;
    }
    this.requesting = true;
    this.changeDetectorRef.detectChanges();
    const data: {
      page?: number;
      user_id?: number;
      group_id?: number;
    } = { page: this.page };
    if (this.isGroup) {
      data.group_id = this.chat.chatter_id;
    } else {
      data.user_id = this.chat.chatter_id;
    }
    this.chatService
      .messages(data)
      .subscribe(
        {
          next: (response) => {
            if (response.chatter) {
              this.chat.chatter = response.chatter;
            }
            if (!this.messages || reset) {
              this.messages = [];
            }
            if (response.messages.next_page_url) {
              this.page = response.messages.current_page + 1;
            } else {
              this.page = 0;
            }
            response.messages.data.forEach(message => {
              this.messages.unshift(message);
            });
          },
          error: (error) => this.alertService.error(error)
        }
      )
      .add(() => {
        if (!this.autoScrollLocked) {
          this.scrollToBottom();
          this.autoScrollLocked = true;
        }
        this.requesting = false;
        this.messagesRead();
        this.changeDetectorRef.detectChanges();
      });
  }

  private messagesRead() {
    const messagesIDs = this.messages
      .filter(message =>
        message.author_id !== this.user.id &&
        (!message.read_by || message.read_by.indexOf(this.user.id) < 0)
      )
      .map(message => message.id);
    if (messagesIDs.length) {
      if (this.messagesReadPromise) {
        clearInterval(this.messagesReadPromise);
      }
      this.messagesReadPromise = setTimeout(() => {
        const data: {
          messages: Array<number>;
          user_id?: number;
          group_id?: number;
        } = { messages: messagesIDs };
        if (this.isGroup) {
          data.group_id = this.chat.chatter_id;
        } else {
          data.user_id = this.chat.chatter_id;
        }
        this.chatService
          .messagesRead(data)
          .subscribe();
      }, 3000);
    }
  }

  private scrollToBottom(): void {
    setTimeout(
      () => {
        const element = document.getElementById('messagesContainer');
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      });
  }

  public inputFocusIn() {
    setTimeout(() => this.scrollToBottom(), 777);
  }

  public back() {
    this.dismiss.emit();
  }

  public submit(event?: MouseEvent) {
    event?.stopPropagation();
    event?.preventDefault();
    this.error = null;
    if (!this.message?.trim()?.length) {
      this.message = '';
      return;
    }
    if (this.requesting) {
      return;
    }
    const data: {
      message: string;
      user_id?: number;
      group_id?: number;
    } = { message: this.message };
    if (this.chat.chatter_type === ChatterType.User) {
      data.user_id = this.chat.chatter_id;
    } else {
      data.group_id = this.chat.chatter_id;
    }
    this.requesting = true;
    this.changeDetectorRef.detectChanges();
    this.chatService
      .sendMessage(data)
      .subscribe({
        next: (response) => {
          this.message = '';
          if (!this.messages?.some(message => message.id === response.message.id)) {
            this.messages.push(response.message);
            this.scrollToBottom();
          }
        },
        error: (error) => this.error = StringHelper.error(error)
      }
      )
      .add(() => {
        this.requesting = false;
        this.changeDetectorRef.detectChanges();
      });
  }

  private userOrGroup() {
    if (this.isGroup) {
      return {
        id: this.chat.chatter_id,
        type: 'group',
      };
    }
    return {
      id: this.user.id,
      type: 'user',
    }
  }

  private messageSentListener() {
    const data = this.userOrGroup();
    this.messageSentSubscription =
      this.messageSentService
        .listener(data)
        .subscribe(response => {
          if (
            ((
              response.message?.chatter_type === ChatterType.Group &&
              this.chat.chatter_id === response.message?.chatter_id
            ) || (
                response.message?.chatter_type === ChatterType.User &&
                this.chat.chatter_id === response.message?.author_id
              )) &&
            !this.messages?.some(message => message.id === response.message.id)
          ) {
            this.messages.push(response.message);
            this.scrollToBottom();
            this.messagesRead();
            this.changeDetectorRef.detectChanges();
          }
        });
  }

  public messageReadListeners() {
    this.messageReadSubscription = this.messageReadService
      .listener(this.userOrGroup())
      .subscribe(response => this.updateReadMessages(response.message_ids));
  }

  private updateReadMessages(ids?: Array<number>) {
    ids?.forEach(id => {
      const currentMessage = this.messages.find(message => message.id === id);
      if (currentMessage) {
        if (!currentMessage.read_by) {
          currentMessage.read_by = [];
        }
      }
    });
    this.changeDetectorRef.detectChanges();
  }

  public answer(message: IMessage, value: boolean) {
    if (message.key === 'requestBringIn' && message.metadata.shift_id) {
      this.requesting = true;
      this.changeDetectorRef.detectChanges();
      return this.shiftService
        .answerBringIn(
          message.id,
          value,
          message.metadata.shift_id
        )
        .subscribe({
          next: (response) => {
            message.answer = value;
            this.alertService.toast(response.message);
          },
          error: (error) => this.alertService.error(error)
        }
        )
        .add(() => {
          this.requesting = false;
          this.changeDetectorRef.detectChanges();
        });
    }
    this.alertService.show('Something went wrong.');
  }

  public yesNoDisabled(message: IMessage): boolean {
    if (this.requesting) {
      return true;
    }
    if (message?.answer === true || message?.answer === false) {
      return true;
    }
    return false;
  }

  protected hideKeyboard(): void {
    this.blockInputFocus = true;
    Keyboard.hide();
    setTimeout(() => this.blockInputFocus = false);
  }
}
